import { motion } from 'framer-motion';
import { 
  RotateCcw, Box, Layers, Wrench, ArrowRight, 
  Settings, Ruler, Microscope, Plane, Car, Zap
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { CTASection } from '@/components/sections/CTASection';
import { FaqSection } from '@/components/sections/FaqSection';
import { ServiceDetailCard } from '@/components/sections/ServiceDetailCard';
import { RelatedLandings } from '@/components/sections/RelatedLandings';
import { getBreadcrumbJsonLd, getServiceJsonLd, getFaqJsonLd, BASE_URL } from '@/lib/seo-constants';
import { useT, useLang } from '@/lib/i18n';
import { LocalizedLink } from '@/components/LocalizedLink';
import leistungenDrehen from '@/assets/leistungen-drehen.webp';
import leistungenFraesen from '@/assets/leistungen-fraesen.webp';
import precisionParts from '@/assets/precision-parts.webp';
import leistungenCadcam from '@/assets/leistungen-cadcam.webp';

const servicesDE = [
  {
    id: 'drehen',
    icon: RotateCcw,
    title: 'CNC-Drehen',
    subtitle: 'Hochpräzise Drehteile',
    description: 'Mit unseren EMCO CNC-Drehmaschinen fertigen wir präzise Drehteile in allen gängigen Materialien. Angetriebene Werkzeuge, Y-Achse und automatischer Stangenlader ermöglichen die Komplettbearbeitung in einer Aufspannung.',
    image: leistungenDrehen,
    features: [
      'Stangendurchlass bis Ø 65 mm',
      'Angetriebene Werkzeuge',
      'Y-Achsen-Bearbeitung (ab E65 Bj. 2017)',
      'C-Achse (E25)',
      'Automatischer Stangenlader',
      'Siemens Sinumerik 828D Steuerung',
    ],
    specs: [
      { label: 'Max. Stangendurchlass', value: 'Ø 65 mm' },
      { label: 'Max. Drehdurchmesser', value: 'Ø 150 mm' },
      { label: 'Max. Verfahrweg X', value: '260 mm' },
      { label: 'Max. Verfahrweg Z', value: '610 mm' },
      { label: 'Spindeldrehzahl', value: 'bis 6.300 U/min' },
    ],
    machines: ['3× EMCO TURN E65', '1× EMCO TURN E25'],
  },
  {
    id: 'fraesen',
    icon: Box,
    title: 'CNC-Fräsen',
    subtitle: 'Komplexe 3D-Geometrien',
    description: 'Unsere HURCO Bearbeitungszentren ermöglichen die Fertigung komplexer 3D-Geometrien mit höchster Genauigkeit. Mit 4. und 5. Achse (Rundtisch) realisieren wir auch anspruchsvollste Bauteile.',
    image: leistungenFraesen,
    features: [
      '3- und 5-Achsen-Bearbeitung',
      'Verfahrwege bis 1.067 mm',
      'HURCO Ultimax 4 & WinMax Steuerung',
      'Werkzeugwechsler bis 40 Plätze',
      'Spindeldrehzahl bis 12.000 U/min',
      'Einzelteile bis Kleinserien',
    ],
    specs: [
      { label: 'Max. Verfahrweg X', value: '1.067 mm' },
      { label: 'Max. Verfahrweg Y', value: '610 mm' },
      { label: 'Max. Verfahrweg Z', value: '610 mm' },
      { label: 'Achsen', value: 'bis 5' },
    ],
    machines: ['HURCO VMX 30t', 'HURCO VMX 30i', 'HURCO VMX 42t'],
  },
  {
    id: 'baugruppen',
    icon: Layers,
    title: 'Baugruppen',
    subtitle: 'Alles aus einer Hand',
    description: 'Von der Einzelkomponente zur kompletten Baugruppe. Wir übernehmen den gesamten Prozess von der Konstruktion über die Fertigung bis zur Montage und Inbetriebnahme.',
    image: precisionParts,
    features: [
      'Konstruktion in SolidWorks',
      'Fertigung aller Komponenten',
      'Präzise Montage',
      'Qualitätskontrolle',
      'Dokumentation',
    ],
    specs: [
      { label: 'Prozess', value: 'End-to-End' },
      { label: 'CAD-System', value: 'SolidWorks' },
      { label: 'Lieferung', value: 'Regional & Bundesweit' },
    ],
    machines: [],
  },
  {
    id: 'cad',
    icon: Wrench,
    title: 'CAD/CAM & Weitere Leistungen',
    subtitle: 'Komplettservice',
    description: 'Neben der eigenen Fertigung bieten wir CAD/CAM-Konstruktion und koordinieren externe Prozesse wie Erodieren, Schleifen und Oberflächenbehandlungen.',
    image: leistungenCadcam,
    features: [
      'CAD/CAM in SolidWorks',
      'Draht- & Senkerodieren',
      'Rund- & Flachschleifen',
      'Eloxal & Wärmebehandlung',
      'Galvanische Oberflächen',
      'Chemische Veredelung',
    ],
    specs: [
      { label: 'CAD/CAM', value: 'SolidWorks' },
      { label: 'Extern', value: 'Vollständig koordiniert' },
      { label: 'Oberflächengüte', value: 'Nach Kundenwunsch' },
      { label: 'Zertifizierung', value: 'ISO 9001' },
    ],
    machines: [],
  },
];

const servicesEN = [
  {
    id: 'drehen',
    icon: RotateCcw,
    title: 'CNC Turning',
    subtitle: 'High-precision turned parts',
    description: 'Our EMCO CNC lathes produce precise turned parts in all common materials. Live tooling, Y-axis and an automatic bar loader enable complete machining in a single setup.',
    image: leistungenDrehen,
    features: [
      'Bar capacity up to Ø 65 mm',
      'Live tooling',
      'Y-axis machining (E65 from 2017)',
      'C-axis (E25)',
      'Automatic bar loader',
      'Siemens Sinumerik 828D control',
    ],
    specs: [
      { label: 'Max. bar capacity', value: 'Ø 65 mm' },
      { label: 'Max. turning diameter', value: 'Ø 150 mm' },
      { label: 'Max. travel X', value: '260 mm' },
      { label: 'Max. travel Z', value: '610 mm' },
      { label: 'Spindle speed', value: 'up to 6,300 rpm' },
    ],
    machines: ['3× EMCO TURN E65', '1× EMCO TURN E25'],
  },
  {
    id: 'fraesen',
    icon: Box,
    title: 'CNC Milling',
    subtitle: 'Complex 3D geometries',
    description: 'Our HURCO machining centres deliver complex 3D geometries with the highest accuracy. With 4th and 5th axis (rotary table) we realise even the most demanding components.',
    image: leistungenFraesen,
    features: [
      '3- and 5-axis machining',
      'Travels up to 1,067 mm',
      'HURCO Ultimax 4 & WinMax control',
      'Tool changer up to 40 pockets',
      'Spindle speed up to 12,000 rpm',
      'Single parts to small series',
    ],
    specs: [
      { label: 'Max. travel X', value: '1,067 mm' },
      { label: 'Max. travel Y', value: '610 mm' },
      { label: 'Max. travel Z', value: '610 mm' },
      { label: 'Axes', value: 'up to 5' },
    ],
    machines: ['HURCO VMX 30t', 'HURCO VMX 30i', 'HURCO VMX 42t'],
  },
  {
    id: 'baugruppen',
    icon: Layers,
    title: 'Assemblies',
    subtitle: 'Everything from one source',
    description: 'From individual components to complete assemblies. We handle the entire process from design and manufacturing to assembly and commissioning.',
    image: precisionParts,
    features: [
      'Design in SolidWorks',
      'Manufacturing of all components',
      'Precision assembly',
      'Quality control',
      'Documentation',
    ],
    specs: [
      { label: 'Process', value: 'End-to-End' },
      { label: 'CAD system', value: 'SolidWorks' },
      { label: 'Delivery', value: 'Regional & nationwide' },
    ],
    machines: [],
  },
  {
    id: 'cad',
    icon: Wrench,
    title: 'CAD/CAM & Additional Services',
    subtitle: 'Full service',
    description: 'In addition to in-house manufacturing, we offer CAD/CAM design and coordinate external processes such as EDM, grinding and surface treatment.',
    image: leistungenCadcam,
    features: [
      'CAD/CAM in SolidWorks',
      'Wire & sinker EDM',
      'Cylindrical & surface grinding',
      'Anodising & heat treatment',
      'Electroplated surfaces',
      'Chemical finishing',
    ],
    specs: [
      { label: 'CAD/CAM', value: 'SolidWorks' },
      { label: 'External', value: 'Fully coordinated' },
      { label: 'Surface finish', value: 'To customer spec' },
      { label: 'Certification', value: 'ISO 9001' },
    ],
    machines: [],
  },
];

const materialsDE = [
  'Stahl', 'Edelstahl', 'Aluminium', 'Messing', 'Kupfer', 
  'Bronze', 'Titan', 'Kunststoffe', 'NE-Metalle'
];
const materialsEN = [
  'Steel', 'Stainless steel', 'Aluminium', 'Brass', 'Copper',
  'Bronze', 'Titanium', 'Plastics', 'Non-ferrous metals',
];

const industriesDE = [
  { icon: Settings, name: 'Maschinenbau' },
  { icon: Microscope, name: 'Medizintechnik' },
  { icon: Ruler, name: 'Messtechnik' },
  { icon: Plane, name: 'Luftfahrt' },
  { icon: Car, name: 'Automotive' },
  { icon: Zap, name: 'Energietechnik' },
];
const industriesEN = [
  { icon: Settings, name: 'Mechanical engineering' },
  { icon: Microscope, name: 'Medical technology' },
  { icon: Ruler, name: 'Metrology' },
  { icon: Plane, name: 'Aerospace' },
  { icon: Car, name: 'Automotive' },
  { icon: Zap, name: 'Energy technology' },
];

export default function LeistungenPage() {
  const t = useT();
  const lang = useLang();
  const services = lang === 'en' ? servicesEN : servicesDE;
  const materials = lang === 'en' ? materialsEN : materialsDE;
  const industries = lang === 'en' ? industriesEN : industriesDE;

  const jsonLd = [
    getBreadcrumbJsonLd([{ name: t('Leistungen', 'Services'), path: '/leistungen' }], lang),
    getServiceJsonLd({ name: t('CNC-Drehen', 'CNC Turning'), description: t('Hochpräzise CNC-Drehteile auf 4 EMCO Drehmaschinen. Stangendurchlass bis Ø 65 mm, angetriebene Werkzeuge und Y-Achse für Komplettbearbeitung.', 'High-precision CNC turned parts on 4 EMCO lathes. Bar capacity up to Ø 65 mm, live tooling and Y-axis for complete machining.'), url: `${BASE_URL}/leistungen#drehen` }, lang),
    getServiceJsonLd({ name: t('CNC-Fräsen', 'CNC Milling'), description: t('3- bis 5-Achsen CNC-Fräsen auf 3 HURCO Bearbeitungszentren (VMX 30t, 30i, 42t). Verfahrwege bis 1.067 mm.', '3- to 5-axis CNC milling on 3 HURCO machining centres (VMX 30t, 30i, 42t). Travels up to 1,067 mm.'), url: `${BASE_URL}/leistungen#fraesen` }, lang),
    getServiceJsonLd({ name: t('Baugruppenfertigung', 'Assembly manufacturing'), description: t('Komplette Baugruppen aus einer Hand: Konstruktion, Fertigung, Montage und Qualitätskontrolle.', 'Complete assemblies from one source: design, manufacturing, assembly and quality control.'), url: `${BASE_URL}/leistungen#baugruppen` }, lang),
    getServiceJsonLd({ name: t('CAD/CAM & Weitere Leistungen', 'CAD/CAM & Additional Services'), description: t('CAD/CAM-Konstruktion in SolidWorks sowie koordinierte externe Prozesse: Erodieren, Schleifen, Oberflächenbehandlung.', 'CAD/CAM design in SolidWorks and coordinated external processes: EDM, grinding, surface treatment.'), url: `${BASE_URL}/leistungen#cad` }, lang),
    getFaqJsonLd(lang === 'en' ? [
      { question: 'Which CNC machines does FERNAU use?', answer: 'FERNAU uses three HURCO milling centres (VMX 30t, VMX 30i, VMX 42t) with up to 5 axes and four EMCO lathes (3× E65, 1× E25) with Siemens Sinumerik 828D control. The machine park is complemented by conventional lathes and a TRUMPF laser.' },
      { question: 'Which materials does FERNAU machine?', answer: 'Steel, stainless steel, aluminium, brass, copper, bronze, titanium, plastics and other non-ferrous metals.' },
      { question: 'Which industries does FERNAU manufacture for?', answer: 'FERNAU manufactures across industries including mechanical engineering, medical technology, metrology, aerospace, automotive, energy technology and many more.' },
    ] : [
      { question: 'Welche CNC-Maschinen setzt FERNAU ein?', answer: 'FERNAU nutzt drei HURCO Fräszentren (VMX 30t, VMX 30i, VMX 42t) mit bis zu 5 Achsen sowie vier EMCO Drehmaschinen (3× E65, 1× E25) mit Siemens Sinumerik 828D Steuerung. Ergänzt wird der Maschinenpark durch konventionelle Drehmaschinen und einen TRUMPF Laser.' },
      { question: 'Welche Materialien bearbeitet FERNAU?', answer: 'Stahl, Edelstahl, Aluminium, Messing, Kupfer, Bronze, Titan, Kunststoffe und weitere NE-Metalle.' },
      { question: 'Für welche Branchen fertigt FERNAU?', answer: 'FERNAU fertigt branchenübergreifend für verschiedenste Industrien, darunter Maschinenbau, Medizintechnik, Messtechnik, Luftfahrt, Automotive, Energietechnik und viele weitere Bereiche.' },
    ], lang),
  ];

  return (
    <Layout>
      <SEOHead
        title={{ de: 'Leistungen – CNC-Drehen, Fräsen & Baugruppen', en: 'Services – CNC Turning, Milling & Assemblies' }}
        description={{ de: 'CNC-Drehen bis Ø200mm, 5-Achsen-Fräsen auf HURCO-Zentren, Baugruppenfertigung und CAD/CAM. ISO 9001 zertifiziert in Darmstadt.', en: 'CNC turning up to Ø200 mm, 5-axis milling on HURCO centres, assembly manufacturing and CAD/CAM. ISO 9001 certified in Darmstadt, Germany.' }}
        path="/leistungen"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.span
            className="text-primary font-mono text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {t('Unser Leistungsspektrum', 'Our range of services')}
          </motion.span>
          <motion.h1
            className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t(<>Präzision in <span className="text-primary">jeder Dimension</span></>, <>Precision in <span className="text-primary">every dimension</span></>)}
          </motion.h1>
          <motion.p
            className="mt-6 text-xl text-muted-foreground max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {t(
              'Vom Einzelteil zur Kleinserie, von der Konstruktion bis zur Inbetriebnahme. Wir sind Ihr Partner für hochpräzise CNC-Zerspanung.',
              'From single parts to small series, from design to commissioning. We are your partner for high-precision CNC machining.'
            )}
          </motion.p>
        </div>
      </section>

      {/* Ansprechpartner */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              className="p-8 rounded-2xl bg-background border border-border/40 flex flex-col sm:flex-row items-center gap-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-primary/10 border-2 border-primary/20 flex items-center justify-center shrink-0">
                <span className="text-4xl md:text-5xl font-bold text-primary">NR</span>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-2xl font-bold">Nicole Römhild</h3>
                <p className="text-primary font-medium mt-1">{t('Ansprechpartnerin Zerspanung', 'Contact – CNC machining')}</p>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                  {t('Ihr direkter Kontakt für alle Fragen rund um CNC-Drehen, Fräsen und Baugruppenfertigung.', 'Your direct contact for all questions about CNC turning, milling and assembly manufacturing.')}
                </p>
                <div className="mt-5">
                  <Button variant="hero" size="lg" asChild>
                    <LocalizedLink to="/kontakt">
                      {t('Kontakt aufnehmen', 'Get in touch')}
                      <ArrowRight className="w-4 h-4" />
                    </LocalizedLink>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Materials Bar */}
      <section className="py-8 bg-secondary/50 border-y border-border/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <span className="text-sm text-muted-foreground">{t('Materialien:', 'Materials:')}</span>
            {materials.map((material) => (
              <span key={material} className="spec-badge technical-data">
                {material}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="space-y-24 lg:space-y-32">
            {services.map((service, index) => (
              <ServiceDetailCard key={service.id} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-bold">{t('Branchen & Anwendungen', 'Industries & Applications')}</h2>
            <p className="mt-4 text-muted-foreground">
              {t('Unsere Präzisionsteile finden Einsatz in anspruchsvollsten Industrien.', 'Our precision parts are used in the most demanding industries.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {industries.map((industry) => (
              <motion.div
                key={industry.name}
                className="card-industrial p-8 text-center"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                  <industry.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="mt-4 text-xl font-semibold">{industry.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection
        subtitle={t('Antworten auf die wichtigsten Fragen zu unserer CNC-Fertigung.', 'Answers to the most important questions about our CNC manufacturing.')}
        faqs={lang === 'en' ? [
          { question: 'Which CNC machines does FERNAU use?', answer: 'We work with three HURCO milling centres (VMX 30t, VMX 30i, VMX 42t) for 3- to 5-axis machining and four EMCO lathes (3× E65, 1× E25) with Siemens Sinumerik 828D control. Our machine park is complemented by conventional lathes and a TRUMPF laser.' },
          { question: 'Which materials does FERNAU machine?', answer: 'We process steel, stainless steel, aluminium, brass, copper, bronze, titanium, engineering plastics and other non-ferrous metals. The material choice is matched individually to the requirements of your part.' },
          { question: 'Which industries does FERNAU manufacture for?', answer: 'We manufacture across industries – from mechanical engineering and medical technology, through metrology and aerospace, to automotive, energy technology and many more. The ISO 9001:2015 certification ensures consistently high quality standards.' },
          { question: 'Does FERNAU also produce single parts?', answer: 'Yes, we produce both single parts and small series. Thanks to flexible setup processes and modern CAD/CAM programming, even short-notice orders can be realised economically.' },
          { question: 'How does an enquiry with FERNAU work?', answer: 'Send us your drawing or 3D model by email or via our contact form. We check feasibility, prepare a quote and agree on schedule and details with you personally.' },
        ] : [
          { question: 'Welche CNC-Maschinen setzt FERNAU ein?', answer: 'Wir arbeiten mit drei HURCO Fräszentren (VMX 30t, VMX 30i, VMX 42t) für 3- bis 5-Achs-Bearbeitung sowie vier EMCO Drehmaschinen (3× E65, 1× E25) mit Siemens Sinumerik 828D Steuerung. Ergänzt wird unser Maschinenpark durch konventionelle Drehmaschinen und einen TRUMPF Laser.' },
          { question: 'Welche Materialien bearbeitet FERNAU?', answer: 'Wir verarbeiten Stahl, Edelstahl, Aluminium, Messing, Kupfer, Bronze, Titan, technische Kunststoffe und weitere NE-Metalle. Die Materialwahl stimmen wir individuell auf die Anforderungen Ihres Bauteils ab.' },
          { question: 'Für welche Branchen fertigt FERNAU?', answer: 'Wir fertigen branchenübergreifend für verschiedenste Industrien – von Maschinenbau und Medizintechnik über Messtechnik und Luftfahrt bis hin zu Automotive, Energietechnik und vielen weiteren Bereichen. Die ISO 9001:2015 Zertifizierung gewährleistet durchgängig hohe Qualitätsstandards.' },
          { question: 'Fertigt FERNAU auch Einzelteile?', answer: 'Ja, wir fertigen sowohl Einzelteile als auch Kleinserien. Dank flexibler Rüstprozesse und moderner CAD/CAM-Programmierung sind auch kurzfristige Aufträge wirtschaftlich realisierbar.' },
          { question: 'Wie läuft eine Anfrage bei FERNAU ab?', answer: 'Senden Sie uns Ihre Zeichnung oder Ihr 3D-Modell per E-Mail oder über unser Kontaktformular. Wir prüfen die Machbarkeit, erstellen ein Angebot und stimmen Termine sowie Details persönlich mit Ihnen ab.' },
        ]}
      />

      <RelatedLandings
        headingDe="CNC-Fertigung nach Kategorie & Region"
        headingEn="CNC manufacturing by category & region"
        introDe="Wir liefern deutschlandweit, in ganz Europa und weltweit per Luftfracht. Wählen Sie Ihre Kategorie oder Region."
        introEn="We ship across Germany, throughout Europe and worldwide by air freight. Pick your category or region."
        links={[
          { href: '/cnc-fraesen-deutschland', labelDe: 'CNC-Fräsen Deutschland', labelEn: 'CNC milling Germany', descDe: '3- bis 5-Achs-Bearbeitung, deutschlandweiter Versand in 24–48h.', descEn: '3- to 5-axis milling with 24–48h shipping across Germany.' },
          { href: '/cnc-drehen-deutschland', labelDe: 'CNC-Drehen Deutschland', labelEn: 'CNC turning Germany', descDe: 'Präzisionsdrehteile bis Ø 65 mm auf EMCO-Maschinen.', descEn: 'Precision turned parts up to Ø 65 mm on EMCO lathes.' },
          { href: '/cnc-lohnfertigung', labelDe: 'CNC-Lohnfertigung', labelEn: 'CNC contract manufacturing', descDe: 'Einzelteile, Kleinserien, Rahmenverträge – ISO 9001:2015.', descEn: 'Single parts, small series, framework agreements – ISO 9001:2015.' },
          { href: '/cnc-fertigung-rhein-main', labelDe: 'Rhein-Main', labelEn: 'Rhine-Main region', descDe: 'Standort Darmstadt – Frankfurt, Wiesbaden, Mainz in 24h.', descEn: 'Based in Darmstadt – Frankfurt, Wiesbaden, Mainz within 24h.' },
          { href: '/cnc-fertigung-stuttgart', labelDe: 'Region Stuttgart', labelEn: 'Stuttgart region', descDe: 'Frästeile & Drehteile für den Maschinenbau in Baden-Württemberg.', descEn: 'Milled and turned parts for mechanical engineering in Baden-Württemberg.' },
          { href: '/cnc-fertigung-muenchen', labelDe: 'Region München', labelEn: 'Munich region', descDe: 'Präzisionsteile für Aerospace, Medizintechnik & Automotive.', descEn: 'Precision parts for aerospace, medical technology and automotive.' },
          { href: '/cnc-fertigung-hamburg', labelDe: 'Region Hamburg', labelEn: 'Hamburg region', descDe: 'Baugruppen und Serienteile für die norddeutsche Industrie.', descEn: 'Assemblies and series parts for northern German industry.' },
          { href: '/praezisionsteile-hersteller', labelDe: 'Präzisionsteile-Hersteller', labelEn: 'Precision parts manufacturer', descDe: 'Toleranzen bis ±5 µm, CMM-Berichte, Werkszeugnisse EN 10204 3.1.', descEn: 'Tolerances to ±5 µm, CMM reports, EN 10204 3.1 certificates.' },
          { href: '/zerspanung-auftragsfertigung', labelDe: 'Auftragsfertigung', labelEn: 'Contract machining', descDe: 'Vom Einzelteil bis zur wiederkehrenden Serienproduktion.', descEn: 'From single parts to recurring series production.' },
        ]}
      />

      <CTASection />
    </Layout>
  );
}
